import { cn } from '@shared/lib/cn'
import { Button } from '@shared/ui/button'
import { HeroLayout } from '@shared/ui/hero-layout'
import { Input } from '@shared/ui/input'
import { motion } from 'motion/react'

interface Step1GenderNameProps {
  readonly gender: string
  readonly onGenderChange: (v: string) => void
  readonly name: string
  readonly onNameChange: (v: string) => void
  readonly onBack: () => void
  readonly onNext: () => void
  readonly hasSavedInfo?: boolean
  readonly onSkipToConfirm?: () => void
}

export function Step1GenderName({
  gender,
  onGenderChange,
  name,
  onNameChange,
  onBack,
  onNext,
  hasSavedInfo,
  onSkipToConfirm,
}: Step1GenderNameProps) {
  return (
    <HeroLayout
      characterImage="/character-assets/2_form_cleaned.png"
      progressStep={1}
      title="당신이 궁금해요"
      subtitle="정확한 사주 풀이를 위해 알려주세요"
      onBack={onBack}
      footer={
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!gender || name.length === 0}
          onClick={onNext}
        >
          다음
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Gender */}
        <div className="flex flex-col gap-2">
          <span className="typo-caption font-medium text-muted-foreground">성별</span>
          <div className="flex gap-2.5">
            {[
              { label: '남자', value: 'MALE' },
              { label: '여자', value: 'FEMALE' },
            ].map((opt) => {
              const isSelected = gender === opt.value
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={cn(
                    'flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl py-3.5 typo-label font-semibold transition-all',
                    isSelected
                      ? 'border border-primary bg-primary/10 text-foreground'
                      : 'border border-border bg-bg-surface text-muted-foreground',
                  )}
                  onClick={() => {
                    onGenderChange(opt.value)
                  }}
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Name */}
        <div className="flex flex-col gap-2">
          <span className="typo-caption font-medium text-muted-foreground">이름</span>
          <Input
            id="name"
            placeholder="이름을 입력해주세요"
            className="h-12 rounded-xl typo-body"
            value={name}
            onChange={(e) => {
              onNameChange(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (gender && name.length > 0) {
                  onNext()
                }
              }
            }}
          />
        </div>

        {/* 저장 정보 스킵 */}
        {hasSavedInfo && onSkipToConfirm && (
          <motion.button
            type="button"
            className="-mt-2 cursor-pointer py-1 text-center typo-caption text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
            onClick={onSkipToConfirm}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            이전 정보 그대로 볼게요
          </motion.button>
        )}
      </div>
    </HeroLayout>
  )
}
